import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseFormControl } from '../base-control';
import { FormDateField } from '../form-field';

@Component({
    standalone: false,
    selector: 'abs-date-picker',
    template: `
        <div *ngIf="parentFormGroup" [formGroup]="parentFormGroup">
            <div class="form-group" *ngIf="!field.multi">
                <label *ngIf="!field.hideLabel"
                    >{{ field.label || (field.name | formLabel)
                    }}<span class="required-marker" *ngIf="field.required" aria-hidden="true">*</span>
                    <ion-icon class="info" *ngIf="field.description" name="information-circle" (click)="showDescription($event)"></ion-icon
                ></label>

                <ion-datetime-button [attr.presentation]="field.presentation || 'date'" [datetime]="fullControlId"></ion-datetime-button>

                <ion-modal [keepContentsMounted]="true">
                    <ng-template>
                        <ion-datetime
                            [id]="fullControlId"
                            [presentation]="field.presentation || 'date'"
                            [formControlName]="field.name"
                            [disabled]="field.readOnly"
                        ></ion-datetime>
                    </ng-template>
                </ion-modal>
            </div>
        </div>
    `,
    styles: [
        `
            ion-icon {
                align-self: center;
                padding-left: 8px;
            }

            .horizontal {
                padding-bottom: 4px;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatePickerComponent extends BaseFormControl implements OnDestroy {
    field: FormDateField;

    fullControlId: string;
    private readonly destroy$ = new Subject<void>();
    ngOnInit(): void {
        super.ngOnInit();
        let controlPath: string[] = [];
        let parent: UntypedFormGroup | null = this.parentFormGroup;
        let controlName = this.field.name;
        controlPath.unshift(controlName);
        let currentGroup = parent;
        while (currentGroup && currentGroup.parent) {
            const parentGroup = currentGroup.parent as UntypedFormGroup;
            // Find the key in parentGroup.controls that matches currentGroup
            const parentKey = Object.keys(parentGroup.controls).find((key) => parentGroup.get(key) === currentGroup);
            if (parentKey) {
                controlPath.unshift(parentKey);
            }
            currentGroup = parentGroup;
        }
        this.fullControlId = controlPath.join('.');

        // If the control value is null/undefined, initialize it with today's date
        const control = this.formControl;
        if (control && (control.value === null || control.value === undefined)) {
            control.setValue(new Date().toISOString());
        }

        // Call field.onChange whenever the value changes
        if (this.field.onChange) {
            control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
                this.field.onChange(value, this.parentFormGroup);
            });
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get formArray() {
        return this.parentFormGroup.get(this.field.name) as UntypedFormArray;
    }

    get formControl() {
        return this.parentFormGroup.get(this.field.name) as UntypedFormControl;
    }
}
